/**
 * Unit basic information feature for the unit contracts page (contacts subpage / property).
 * Injects a "Eigenschaften der Einheit" section as the last element of the target wrapper.
 * URL: /properties/{propertyHrId}/edit/unit/{unitId}/contract/all
 * Fetches from GET /api/unit-basic-info, saves via POST. Skeleton shown while loading.
 */

const unitBasicInfo = () => {
  const CONTAINER_ID = 'lux-unit-basic-info-section'
  const SKELETON_ID = 'lux-unit-basic-info-skeleton'
  const INTERIOR_ROW_KEY_PREFIX = 'interior-row-'
  const targetElementSelector = '#UnitContractHistoryWrapper > .section-wrapper-content > .section-content'

  const getContextFromUrl = () => {
    const path = window.location.pathname
    const propertyMatch = path.match(/\/properties\/([^/]+)\//)
    const unitMatch = path.match(/\/unit\/([^/]+)\//)
    return {
      propertyHrId: propertyMatch ? propertyMatch[1] : null,
      unitId: unitMatch ? unitMatch[1] : null,
    }
  }

  const getAuthToken = () => {
    try {
      const raw = localStorage.getItem('auth')
      return raw ? JSON.parse(raw).token : null
    } catch {
      return null
    }
  }

  const createSection = () => {
    if (document.getElementById(CONTAINER_ID)) return

    const wrapper = document.querySelector(targetElementSelector)
    if (!wrapper) return

    const { propertyHrId, unitId } = getContextFromUrl()
    if (!propertyHrId || !unitId) return

    const section = document.createElement('div')
    section.id = CONTAINER_ID
    section.className = 'SecondarySectionWrapper'
    section.setAttribute('data-testid', 'secondary-section-wrapper-Grunddaten')

    let interiorRowId = 0
    const addInteriorRow = (tbody, data = {}) => {
      const rowKey = `${INTERIOR_ROW_KEY_PREFIX}${++interiorRowId}`
      const tr = document.createElement('tr')
      tr.className = 'ant-table-row'
      tr.setAttribute('data-row-key', rowKey)
      const valueNum = data.value != null && data.value !== '' ? (typeof data.value === 'number' ? data.value : parseFloat(String(data.value).replace(',', '.')) || '') : ''
      tr.innerHTML = `
        <td class="ant-table-cell">
          <input type="text" class="ant-input Input" placeholder="z.B. Einbauküche, Stellplatz mit Wallbox" data-field="title" style="width:100%;max-width:220px;" />
        </td>
        <td class="ant-table-cell">
          <select class="ant-input Input" data-field="type" style="width:100%;max-width:140px;">
            <option value="">—</option>
            <option value="Küche">Küche</option>
            <option value="Möbel">Möbel</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
        </td>
        <td class="ant-table-cell">
          <div class="lux-euro-input" style="display:flex;align-items:center;max-width:140px;">
            <span style="flex-shrink:0;margin-right:4px;">€</span>
            <input type="text" class="ant-input Input" placeholder="0" data-field="value" data-raw-number inputmode="decimal" style="width:100%;" />
          </div>
        </td>
        <td class="ant-table-cell">
          <input type="text" class="ant-input Input" placeholder="Abschreibungsdauer in Monaten" data-field="usagePeriod" style="width:100%;max-width:160px;" />
        </td>
        <td class="ant-table-cell" style="width:50px;">
          <button type="button" class="ant-btn ant-btn-text ant-btn-sm lux-remove-interior-row" aria-label="Zeile entfernen">×</button>
        </td>
      `
      tr.querySelector('[data-field="title"]').value = data.title != null ? data.title : ''
      const typeSelect = tr.querySelector('[data-field="type"]')
      const typeVal = data.type != null ? data.type : ''
      if (typeVal && ![...typeSelect.options].some((o) => o.value === typeVal)) {
        const legacyOpt = document.createElement('option')
        legacyOpt.value = typeVal
        legacyOpt.textContent = typeVal
        legacyOpt.dataset.legacy = 'true'
        typeSelect.insertBefore(legacyOpt, typeSelect.options[1])
        typeSelect.addEventListener('change', function removeLegacy() {
          const opt = typeSelect.querySelector('option[data-legacy="true"]')
          if (opt && typeSelect.value !== opt.value) {
            opt.remove()
            typeSelect.removeEventListener('change', removeLegacy)
          }
        })
      }
      typeSelect.value = typeVal
      tr.querySelector('[data-field="value"]').value = valueNum !== '' ? String(valueNum) : ''
      tr.querySelector('[data-field="usagePeriod"]').value = data.usagePeriod != null ? data.usagePeriod : ''
      const valueInput = tr.querySelector('[data-field="value"]')
      valueInput.addEventListener('input', function () {
        let v = this.value.replace(/[^\d,.-]/g, '').replace(',', '.')
        const parts = v.split('.')
        if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('')
        this.value = v
      })
      tr.querySelector('.lux-remove-interior-row').addEventListener('click', () => tr.remove())
      tbody.appendChild(tr)
    }

    const tableId = 'UnitBasicInfoInteriorTable'
    section.innerHTML = `
      <div class="ant-collapse ant-collapse-icon-position-right ant-collapse-ghost ant-collapse-subsection-wrapper">
        <div class="ant-collapse-item ant-collapse-item-active">
          <div class="ant-collapse-header" role="button" tabindex="-1" aria-expanded="true">
            <h3 class="subsection-title-wrapper">
              <span class="header">
                <span class="title-subtitle" role="button" tabindex="0">Eigenschaften der Einheit</span>
                <span class="collapse-icon-wrapper"></span>
              </span>
            </h3>
          </div>
          <div class="ant-collapse-content ant-collapse-content-active">
            <div class="ant-collapse-content-box">
              <div id="${SKELETON_ID}" class="lux-unit-basic-info-skeleton" style="display:block;padding:8px 0 16px 0;">
                <div style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:16px;">
                  <div style="display:flex;gap:24px;align-items:flex-end;flex-wrap:nowrap;">
                    <div>
                      <div style="height:14px;width:92px;background:#f4f4f4;border-radius:4px;margin-bottom:8px;"></div>
                      <div style="height:32px;width:80px;background:#f4f4f4;border-radius:4px;"></div>
                    </div>
                    <div>
                      <div style="height:14px;width:102px;background:#f4f4f4;border-radius:4px;margin-bottom:8px;"></div>
                      <div style="height:32px;width:80px;background:#f4f4f4;border-radius:4px;"></div>
                    </div>
                    <div>
                      <div style="height:14px;width:88px;background:#f4f4f4;border-radius:4px;margin-bottom:8px;"></div>
                      <div style="height:32px;width:200px;background:#f4f4f4;border-radius:4px;"></div>
                    </div>
                    <div>
                      <div style="height:14px;width:140px;background:#f4f4f4;border-radius:4px;margin-bottom:8px;"></div>
                      <div style="height:32px;width:80px;background:#f4f4f4;border-radius:4px;"></div>
                    </div>
                  </div>
                  <div style="height:32px;width:176px;background:#f4f4f4;border-radius:4px;"></div>
                </div>
                <div style="border-radius:4px;overflow:hidden;background:#fff;">
                  <div style="height:38px;background:#f4f4f4;"></div>
                  <div style="height:44px;background:#f4f4f4;"></div>
                  <div style="height:44px;background:#f4f4f4;"></div>
                  <div style="height:44px;background:#f4f4f4;"></div>
                </div>
                <div style="margin-top:16px;height:32px;width:100%;background:#f4f4f4;border-radius:4px;"></div>
              </div>

              <div class="lux-unit-basic-info-content" style="display:none;">
                <div class="lux-unit-basic-fields" style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:16px;">
                  <div style="display:flex;gap:24px;align-items:flex-end;flex-wrap:nowrap;min-width:40%;">
                    <div class="ant-form-item">
                      <label class="ant-form-item-label" for="lux-unit-rooms" style="display:block;font-size:14px;line-height:14px;margin-bottom:8px;">Anzahl Zimmer</label>
                      <div class="ant-input-number InputNumber Input" style="width:80px;">
                        <div class="ant-input-number-input-wrap">
                          <input type="number" id="lux-unit-rooms" class="ant-input-number-input" min="0" step="1" placeholder="—" />
                        </div>
                      </div>
                    </div>
                    <div class="ant-form-item">
                      <label class="ant-form-item-label" for="lux-unit-balconies" style="display:block;font-size:14px;line-height:14px;margin-bottom:8px;">Anzahl Balkone</label>
                      <div class="ant-input-number InputNumber Input" style="width:80px;">
                        <div class="ant-input-number-input-wrap">
                          <input type="number" id="lux-unit-balconies" class="ant-input-number-input" min="0" step="1" placeholder="—" />
                        </div>
                      </div>
                    </div>
                    <div class="ant-form-item">
                      <label class="ant-form-item-label" for="lux-unit-parking-spots" style="display:block;font-size:14px;line-height:14px;margin-bottom:8px;">Anzahl an Stellplätze</label>
                      <div class="ant-input-number InputNumber Input" style="width:80px;">
                        <div class="ant-input-number-input-wrap">
                          <input type="number" id="lux-unit-parking-spots" class="ant-input-number-input" min="0" step="1" placeholder="—" />
                        </div>
                      </div>
                    </div>
                    <div class="ant-form-item">
                      <label class="ant-form-item-label" for="lux-unit-usage-type" style="display:block;font-size:14px;line-height:14px;margin-bottom:8px;">Nutzungsart</label>
                      <select id="lux-unit-usage-type" class="ant-input-number InputNumber Input" style="width:200px;border-radius:4px;">
                        <option value="">—</option>
                        <option value="Residential">Wohnen</option>
                        <option value="Retail">Einzelhandel</option>
                        <option value="Gastronomy">Gastronomie</option>
                        <option value="Parking">Parken</option>
                        <option value="Vacant">Leerstand</option>
                      </select>
                    </div>
                  </div>
                  <button type="button" id="lux-add-interior-row" class="ant-btn ant-btn-default ant-btn-sm">+ Ausstattung hinzufügen</button>
                </div>
                <div class="TableColumnSelectorWrapper">
                  <div class="ant-table-wrapper UnitContractHistoryTable table-with-minHeight">
                    <div class="ant-spin-nested-loading">
                      <div class="ant-spin-container">
                        <div class="ant-table" id="${tableId}">
                          <div class="ant-table-container">
                            <div class="ant-table-header" style="overflow: hidden;">
                              <table style="table-layout: fixed;">
                                <colgroup>
                                  <col style="min-width:180px;"><col style="min-width:120px;"><col style="min-width:100px;"><col style="min-width:140px;"><col style="width:50px;">
                                </colgroup>
                                <thead class="ant-table-thead">
                                  <tr>
                                    <th class="ant-table-cell">Titel</th>
                                    <th class="ant-table-cell">Typ</th>
                                    <th class="ant-table-cell">Wert</th>
                                    <th class="ant-table-cell">Abschreibungsdauer in Monaten</th>
                                    <th class="ant-table-cell"></th>
                                  </tr>
                                </thead>
                              </table>
                            </div>
                            <div class="ant-table-body" style="overflow-y: auto; max-height: 240px;">
                              <table style="table-layout: fixed;">
                                <colgroup>
                                  <col style="min-width:180px;"><col style="min-width:120px;"><col style="min-width:100px;"><col style="min-width:140px;"><col style="width:50px;">
                                </colgroup>
                                <tbody class="ant-table-tbody"></tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div style="margin-top:16px;width:100%;">
                  <button data-testid="section-save" type="button" class="ant-btn ant-btn-default Button section-submit" id="lux-unit-basic-info-save" style="width:100%;">
                    <span>Speichern und Weiter</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    const skeletonEl = section.querySelector(`#${SKELETON_ID}`)
    const contentEl = section.querySelector('.lux-unit-basic-info-content')
    const contentBoxEl = section.querySelector('.ant-collapse-content-box')
    const tbody = section.querySelector(`#${tableId} .ant-table-tbody`)
    const addButton = section.querySelector('#lux-add-interior-row')
    const saveButton = section.querySelector('#lux-unit-basic-info-save')
    const roomsInput = section.querySelector('#lux-unit-rooms')
    const balconiesInput = section.querySelector('#lux-unit-balconies')
    const usageTypeSelect = section.querySelector('#lux-unit-usage-type')
    const parkingSpotsInput = section.querySelector('#lux-unit-parking-spots')

    addButton.addEventListener('click', () => addInteriorRow(tbody))

    const showContent = (data) => {
      if (data.numberOfRooms != null && data.numberOfRooms !== '') roomsInput.value = data.numberOfRooms
      if (data.numberOfBalconies != null && data.numberOfBalconies !== '') balconiesInput.value = data.numberOfBalconies
      usageTypeSelect.value =
        data.usageType && [...usageTypeSelect.options].some((o) => o.value === data.usageType)
          ? data.usageType
          : ''
      if (data.numberOfParkingSpots != null && data.numberOfParkingSpots !== '') {
        parkingSpotsInput.value = data.numberOfParkingSpots
      }
      tbody.innerHTML = ''
      const items = Array.isArray(data.interior) && data.interior.length > 0 ? data.interior : [{ title: '', type: '', value: null, usagePeriod: '' }]
      items.forEach((item) => addInteriorRow(tbody, item))
      skeletonEl.style.display = 'none'
      contentEl.style.display = 'block'
    }

    const showError = () => {
      skeletonEl.style.display = 'none'
      contentEl.style.display = 'block'
      tbody.innerHTML = ''
      addInteriorRow(tbody)
      const notice = document.createElement('div')
      notice.style.cssText = 'margin-bottom:12px;color:#ff4d4f;font-size:14px;'
      notice.textContent = 'Fehler beim Laden. Sie können die Daten eingeben und speichern.'
      contentBoxEl.prepend(notice)
    }

    const fetchData = () => {
      const token = getAuthToken()
      if (!token) {
        showContent({ interior: [] })
        return
      }
      const url = `${baseApiUrl}/api/unit-basic-info?propertyHrId=${encodeURIComponent(propertyHrId)}&unitId=${encodeURIComponent(unitId)}`
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .then(showContent)
        .catch(() => showError())
    }

    saveButton.addEventListener('click', () => {
      const token = getAuthToken()
      if (!token) {
        alert('Bitte erneut anmelden.')
        return
      }
      const interior = []
      tbody.querySelectorAll('tr').forEach((tr) => {
        const title = (tr.querySelector('[data-field="title"]') || {}).value || ''
        const type = (tr.querySelector('[data-field="type"]') || {}).value || ''
        const valueEl = tr.querySelector('[data-field="value"]')
        const valueStr = (valueEl && valueEl.value) ? valueEl.value.replace(',', '.').trim() : ''
        const valueNum = valueStr === '' ? null : (parseFloat(valueStr));
        const usagePeriod = (tr.querySelector('[data-field="usagePeriod"]') || {}).value || ''
        interior.push({ title, type, value: (valueNum != null && !Number.isNaN(valueNum)) ? valueNum : null, usagePeriod })
      })
      const payload = {
        propertyHrId,
        unitId,
        numberOfRooms: roomsInput.value === '' ? null : parseInt(roomsInput.value, 10),
        numberOfBalconies: balconiesInput.value === '' ? null : parseInt(balconiesInput.value, 10),
        usageType: usageTypeSelect.value === '' ? null : usageTypeSelect.value,
        numberOfParkingSpots: parkingSpotsInput.value === '' ? null : parseInt(parkingSpotsInput.value, 10),
        interior,
      }
      saveButton.disabled = true
      saveButton.querySelector('span').textContent = 'Wird gespeichert…'
      fetch(`${baseApiUrl}/api/unit-basic-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })
        .then((res) => {
          if (res.ok) return res.json()
          return res.json().then((body) => Promise.reject({ status: res.status, body }))
        })
        .then(() => {
          saveButton.querySelector('span').textContent = 'Gespeichert'
          setTimeout(() => {
            saveButton.disabled = false
            saveButton.querySelector('span').textContent = 'Speichern und Weiter'
          }, 1500)
        })
        .catch((err) => {
          saveButton.disabled = false
          saveButton.querySelector('span').textContent = 'Speichern und Weiter'
          let msg = 'Speichern fehlgeschlagen.'
          if (err && err.body && typeof err.body === 'object' && err.body.error) {
            msg = err.body.error
          } else if (err && err.message) {
            msg = err.message
          }
          alert(msg)
        })
    })

    wrapper.appendChild(section)
    fetchData()
  }

  const waitForWrapper = setInterval(() => {
    if (!document.querySelector(targetElementSelector)) return
    clearInterval(waitForWrapper)
    createSection()
  }, 100)
}
